import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import IPInput from 'src/components/ip-input';
import { IVlanItem } from 'src/context/global/types';
import {
  isV4Format,
  validateSubnets,
  isSubnetMaskLength,
  calculateNetworkAddress2,
  convertSubnetMaskToLength,
} from 'src/utils/ip';
import { getErrorIpSubnet } from 'src/utils/validate';
import { twMerge } from 'tailwind-merge';

export default function IpSubnetEditCell({
  getValue,
  row,
  column,
  table,
}: CellContext<IVlanItem, any>) {
  const [errorIp, setErrorIp] = useState('');
  const [errorSubnetMask, setErrorSubnetMask] = useState('');

  const [error, setError] = useState('');

  const handleValidation = (ip: string, subnetMask: string) => {
    if (!ip && !subnetMask) {
      setError('');
      table.options.meta?.updateData(row.index, 'ipSubnet', '', true);
      return;
    }

    const subnet = calculateNetworkAddress2(ip || '', subnetMask || '');
    const validateIpSubnetResult = getErrorIpSubnet({
      ip: ip || '',
      subnet: '',
      subnetMask: subnetMask || '',
      isCheckBroadcastAddress: false,
    });

    let isValid =
      !validateIpSubnetResult.ip &&
      !validateIpSubnetResult.subnet &&
      !validateIpSubnetResult.subnetMask;

    const maskLen = convertSubnetMaskToLength(subnetMask || '');
    const currentCidr = subnet && maskLen > 0 ? `${subnet}/${maskLen}` : '';

    const rows = table.options.data;
    const otherRows = rows.filter((r, index) => index !== row.index);

    let overlaps = false;
    if (currentCidr) {
      overlaps = otherRows.some((r) => {
        const otherSubnet = calculateNetworkAddress2(
          r.ip || '',
          r.subnetMask || '',
        );
        const otherLen = convertSubnetMaskToLength(r.subnetMask || '');
        if (!otherSubnet || otherLen <= 0) return false;
        const otherCidr = `${otherSubnet}/${otherLen}`;
        return !validateSubnets(currentCidr, otherCidr);
      });
      if (overlaps) isValid = false;
    }

    setError(
      validateIpSubnetResult.ip ||
        validateIpSubnetResult.subnet ||
        validateIpSubnetResult.subnetMask ||
        (overlaps ? 'Subnet overlaps with another subnet' : ''),
    );

    table.options.meta?.updateData(row.index, 'ipSubnet', subnet, isValid);
  };

  return (
    <div
      data-testid={`ip-subnet-edit-cell-${row.id}`}
      className="flex w-full flex-col items-start justify-start gap-2"
    >
      <div className="flex w-full flex-row items-start justify-start gap-2">
        <IPInput
          id="ip"
          name="ip"
          wrapperClassName="w-auto"
          placeholder="192.168.1.0"
          value={row.original.ip || ''}
          error={errorIp}
          className={twMerge([errorIp ? 'border-red-500' : ''])}
          onChange={(value) => {
            const updatedValue = value === '...' ? '' : value;
            // validate ip address
            const isValid = updatedValue ? isV4Format(updatedValue) : true;
            setErrorIp(isValid ? '' : '');
            table.options.meta?.setValidRows((old) => ({
              ...old,
              [row.index]: {
                ...old[row.index],
                ip: isValid,
              },
            }));
            table.options.meta?.updateData(
              row.index,
              'ip',
              updatedValue,
              isValid,
            );
            handleValidation(updatedValue, row.original.subnetMask || '');
          }}
          dataTestId={`ip-subnet-edit-cell-ip-input-${column.id}-${row.id}`}
        />
        <span className="mt-2 text-gray-500">/</span>
        <IPInput
          id="netmask"
          name="netmask"
          wrapperClassName="w-auto"
          placeholder="255.255.255.0"
          value={row.original.subnetMask || ''}
          error={errorSubnetMask}
          onChange={(value) => {
            const updatedValue = value === '...' ? '' : value;
            // validate subnet mask length
            const subnetMaskLength = convertSubnetMaskToLength(updatedValue);
            const isValid = updatedValue
              ? isSubnetMaskLength(subnetMaskLength.toString())
              : true;
            setErrorSubnetMask(isValid ? '' : '');
            table.options.meta?.setValidRows((old) => ({
              ...old,
              [row.index]: {
                ...old[row.index],
                subnetMask: isValid,
              },
            }));
            table.options.meta?.updateData(
              row.index,
              'subnetMask',
              updatedValue,
              isValid,
            );
            handleValidation(row.original.ip || '', updatedValue);
          }}
          dataTestId={`ip-subnet-edit-cell-subnet-mask-input-${column.id}-${row.id}`}
        />
      </div>
      {!errorIp && !errorSubnetMask && error && (
        <span
          data-testid={`ip-subnet-edit-cell-error-${column.id}-${row.id}`}
          className="text-red-500"
        >
          {error}
        </span>
      )}
    </div>
  );
}
